import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaArrowRight,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaPaperPlane,
  FaEnvelopeOpenText,
} from "react-icons/fa";
import { Avatar } from "../../OrgUsageAnalytics/components/Avatar";
import {
  fetchAdoptionGapsOverview,
  fetchAdoptionGapsTemplate,
  updateAdoptionGapsTemplate,
  sendTestAdoptionGapsEmail,
  bulkSendAdoptionGaps,
} from "../api";
import { formatRelative } from "../format";
import {
  describeIncompleteScan,
  sortRowsByColumn,
  buildPreviewTokens,
} from "../pcInactivity";
import OutreachModal from "./OutreachModal";

const MIN_DAYS_PRESETS = [
  { label: "All", value: 0 },
  { label: "30+ days", value: 30 },
  { label: "60+ days", value: 60 },
  { label: "90+ days", value: 90 },
];

/** Sortable columns for the never-used table. */
const COLUMNS = {
  NAME: "name",
  DAYS: "daysSinceBaseline",
  FIRST_SEEN: "baselineAt",
};

/**
 * Read-only directory of active organizations that have NEVER adopted a given
 * product surface (investor guidelines, checklist training, or Ask Addy),
 * with the same tooling as the PC inactivity tab: signup-age filters, sortable
 * columns, selection, and a bulk outreach email.
 *
 * The outreach template is per signal, not shared with PC inactivity — the
 * copy has to match the reason the org is on this particular list.
 *
 * @param {{
 *   env: string,
 *   signal: string,
 *   hint: string,
 *   emptyLabel: string,
 *   sendLabel: string,
 *   onSelect: (org: { id: string, name: string }) => void,
 * }} props
 */
export default function NeverUsedOverviewBlock({
  env,
  signal,
  hint,
  emptyLabel,
  sendLabel,
  onSelect,
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [incomplete, setIncomplete] = useState(null);
  const [hideInternal, setHideInternal] = useState(true);
  const [minDays, setMinDays] = useState(0);
  const [sort, setSort] = useState({ key: COLUMNS.DAYS, dir: "desc" });
  const [selected, setSelected] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [templateOnlyOpen, setTemplateOnlyOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setIncomplete(null);
    fetchAdoptionGapsOverview(
      {
        signal,
        limit: "100",
        excludeInternal: String(hideInternal),
        minDaysSinceBaseline: String(minDays),
      },
      env,
    )
      .then((json) => {
        if (cancelled) {
          return;
        }
        setRows(Array.isArray(json?.orgs) ? json.orgs : []);
        setIncomplete(describeIncompleteScan(json));
      })
      .catch((err) => {
        if (cancelled) {
          return;
        }
        setError(err?.message || "Failed to load the directory");
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [env, signal, hideInternal, minDays, reloadKey]);

  // `daysSinceBaseline` is the never-used analogue of "days since last run",
  // so the shared column sorter works unchanged once the key is mapped.
  const sortedRows = useMemo(() => {
    const key = sort.key === COLUMNS.NAME ? "name" : sort.key;
    return sortRowsByColumn(
      rows.map((row) => ({
        ...row,
        daysSinceLastRun: row.daysSinceBaseline,
        lastRunAt: row.baselineAt,
      })),
      key === COLUMNS.DAYS
        ? "daysSinceLastRun"
        : key === COLUMNS.FIRST_SEEN
          ? "lastRunAt"
          : "name",
      sort.dir,
    );
  }, [rows, sort]);

  const applySort = useCallback((key) => {
    setSort((prev) => {
      if (prev.key === key) {
        return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
      }
      return { key, dir: key === COLUMNS.NAME ? "asc" : "desc" };
    });
  }, []);

  const sortIcon = useCallback(
    (key) => {
      if (sort.key !== key) {
        return <FaSort style={{ fontSize: 10, opacity: 0.4 }} />;
      }
      return sort.dir === "asc" ? (
        <FaSortUp style={{ fontSize: 10 }} />
      ) : (
        <FaSortDown style={{ fontSize: 10 }} />
      );
    },
    [sort],
  );

  // Drop selections that are no longer visible after a filter or reload.
  useEffect(() => {
    setSelected((prev) => {
      const visible = new Set(sortedRows.map((r) => r.organizationId));
      const next = new Set([...prev].filter((id) => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [sortedRows]);

  const toggleOne = useCallback((orgId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(orgId)) {
        next.delete(orgId);
      } else {
        next.add(orgId);
      }
      return next;
    });
  }, []);

  const allSelected =
    sortedRows.length > 0 && selected.size === sortedRows.length;

  const toggleAll = useCallback(() => {
    setSelected((prev) =>
      prev.size === sortedRows.length
        ? new Set()
        : new Set(sortedRows.map((r) => r.organizationId)),
    );
  }, [sortedRows]);

  const selectedIds = useMemo(() => [...selected], [selected]);
  const orgNameById = useMemo(
    () =>
      Object.fromEntries(
        rows.map((r) => [r.organizationId, r.organizationName]),
      ),
    [rows],
  );
  const sampleOrg = useMemo(() => {
    const first = sortedRows.find((r) => selected.has(r.organizationId));
    return first
      ? {
          name: first.organizationName,
          daysSinceBaseline: first.daysSinceBaseline,
        }
      : null;
  }, [sortedRows, selected]);

  // Bound to this tab's signal, so each list edits and sends its own copy.
  const outreachApi = useMemo(
    () => ({
      fetchTemplate: (e) => fetchAdoptionGapsTemplate(signal, e),
      updateTemplate: (template, e) =>
        updateAdoptionGapsTemplate(signal, template, e),
      sendTest: (payload, e) =>
        sendTestAdoptionGapsEmail({ ...payload, signal }, e),
      bulkSend: (orgIds, options, e) =>
        bulkSendAdoptionGaps(signal, orgIds, options, e),
    }),
    [signal],
  );

  const outreachCopy = useMemo(
    () => ({
      sendLabel,
      templateBlurb: `Edit and save the ${sendLabel.toLowerCase()}, preview it, or send`,
      tokenHelp: "{{organizationName}}, {{daysSinceSignup}},",
    }),
    [sendLabel],
  );

  const previewTokens = useCallback(
    (org) =>
      buildPreviewTokens({
        organizationName: org?.name,
        daysSinceLastRun: org?.daysSinceBaseline,
      }),
    [],
  );

  return (
    <>
      <div className="oo-landing-head-actions" style={{ marginBottom: 8 }}>
        <div className="oo-seg oo-seg--sm">
          {MIN_DAYS_PRESETS.map((preset) => (
            <button
              key={preset.value}
              type="button"
              className={`oo-seg-btn ${
                minDays === preset.value ? "oo-seg-btn--on" : ""
              }`}
              onClick={() => setMinDays(preset.value)}
            >
              {preset.label}
            </button>
          ))}
        </div>
        <span className="oo-landing-count">{sortedRows.length} org(s)</span>
        <label className="oo-inline-toggle" title="Hide Addy-internal/test orgs">
          <input
            type="checkbox"
            checked={hideInternal}
            onChange={(e) => setHideInternal(e.target.checked)}
          />
          <span>Hide Addy AI orgs</span>
        </label>
        <button
          type="button"
          className="oo-mini-btn"
          onClick={() => setTemplateOnlyOpen(true)}
        >
          <FaEnvelopeOpenText style={{ fontSize: 11 }} /> Email template / test
        </button>
      </div>
      <p className="oo-landing-sort-hint">{hint}</p>

      {!loading && !error && incomplete ? (
        <div className="oo-landing-incomplete">{incomplete}</div>
      ) : null}

      {selected.size > 0 ? (
        <div className="oo-bulk-bar">
          <span>{selected.size} selected</span>
          <div>
            <button
              type="button"
              className="oo-mini-btn"
              onClick={() => setSelected(new Set())}
            >
              Clear
            </button>
            <button
              type="button"
              className="oo-mini-btn oo-mini-btn--primary"
              onClick={() => setBulkOpen(true)}
            >
              <FaPaperPlane style={{ fontSize: 11 }} /> Send to {selected.size}{" "}
              selected
            </button>
          </div>
        </div>
      ) : null}

      {error ? (
        <div className="oo-empty-hint">{error}</div>
      ) : loading ? (
        <div className="oo-skel-stack">
          <div className="ou-skel" style={{ height: 40, marginBottom: 8 }} />
          <div className="ou-skel" style={{ height: 40, marginBottom: 8 }} />
          <div className="ou-skel" style={{ height: 40 }} />
        </div>
      ) : sortedRows.length ? (
        <table className="oo-send-table oo-inactivity-table">
          <thead>
            <tr>
              <th className="oo-check-col">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  aria-label="Select all organizations"
                />
              </th>
              <th
                className="oo-sortable"
                onClick={() => applySort(COLUMNS.NAME)}
              >
                Organization {sortIcon(COLUMNS.NAME)}
              </th>
              <th
                className="oo-sortable"
                onClick={() => applySort(COLUMNS.DAYS)}
              >
                Days since signup {sortIcon(COLUMNS.DAYS)}
              </th>
              <th
                className="oo-sortable"
                onClick={() => applySort(COLUMNS.FIRST_SEEN)}
              >
                First seen {sortIcon(COLUMNS.FIRST_SEEN)}
              </th>
              <th />
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row) => (
              <tr key={row.organizationId}>
                <td className="oo-check-col">
                  <input
                    type="checkbox"
                    checked={selected.has(row.organizationId)}
                    onChange={() => toggleOne(row.organizationId)}
                    aria-label={`Select ${row.organizationName}`}
                  />
                </td>
                <td>
                  <button
                    type="button"
                    className="oo-inactivity-orgbtn"
                    onClick={() =>
                      onSelect({
                        id: row.organizationId,
                        name: row.organizationName,
                      })
                    }
                    title={row.organizationId}
                  >
                    <Avatar
                      name={row.organizationName}
                      seed={row.organizationId}
                      size="sm"
                    />
                    <span>{row.organizationName}</span>
                  </button>
                </td>
                <td>
                  <strong>{row.daysSinceBaseline ?? "—"}</strong>
                </td>
                <td>
                  {row.baselineAt ? formatRelative(row.baselineAt) : "—"}
                </td>
                <td>
                  <FaArrowRight style={{ fontSize: 11, opacity: 0.5 }} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="oo-empty-hint">{emptyLabel}</div>
      )}

      {bulkOpen ? (
        <OutreachModal
          env={env}
          api={outreachApi}
          copy={outreachCopy}
          buildTokens={previewTokens}
          orgIds={selectedIds}
          orgNameById={orgNameById}
          sampleOrg={sampleOrg}
          onClose={() => setBulkOpen(false)}
          onSent={() => {
            setSelected(new Set());
            setReloadKey((k) => k + 1);
          }}
        />
      ) : null}

      {templateOnlyOpen ? (
        <OutreachModal
          env={env}
          api={outreachApi}
          copy={outreachCopy}
          buildTokens={previewTokens}
          orgIds={[]}
          orgNameById={orgNameById}
          sampleOrg={null}
          onClose={() => setTemplateOnlyOpen(false)}
          onSent={() => setTemplateOnlyOpen(false)}
        />
      ) : null}
    </>
  );
}
